import React, {useCallback, useMemo, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {InstrumentedWaveLoader} from '../../../utility/InstrumentedLoader';
import {SettingLabel} from '../components/SettingLabel';
import {BucketCustomization, SemanticLegendSettings} from '../types';
import {BucketCustomizationInputWithCategoricalConfig} from './BucketCustomizationInputWithCategoricalConfig';
import {SemanticLegendBucketsActionsWithCategoricalConfig} from './SemanticLegendBucketsActionsWithCategoricalConfig';
import {SemanticLegendsPaletteWithCategoricalConfig} from './SemanticLegendsPaletteWithCategoricalConfig';
export type SemanticLegendBucketCustomizationProps = {
  buckets: BucketCustomization[];
  bucketCustomizations: Record<number, BucketCustomization>;
  updateSetting: (settings: Partial<SemanticLegendSettings>) => void;
  isLoadingBuckets: boolean;
};

export const SemanticLegendBucketsWithCategoricalConfig = ({
  buckets,
  bucketCustomizations,
  updateSetting,
  isLoadingBuckets,
}: SemanticLegendBucketCustomizationProps) => {
  const ids = useMemo(() => buckets.map(_i => uuidv4()), [buckets]);

  const updateBucketCustomizationSetting = useCallback(
    (settings: Partial<BucketCustomization>, index: number) => {
      const newBucketCustomizations = {...bucketCustomizations};
      newBucketCustomizations[index] = {
        ...newBucketCustomizations[index],
        ...settings,
      };
      updateSetting({
        bucketCustomizations: newBucketCustomizations,
        buckets: buckets,
      });
    },
    [updateSetting, buckets, bucketCustomizations]
  );

  const bucketCustomizationList = buckets?.map((bucket, index) => (
    <li key={ids[index]}>
      <BucketCustomizationInputWithCategoricalConfig
        bucket={bucket}
        bucketCustomization={bucketCustomizations[index] ?? {}}
        index={index}
        numberOfBuckets={buckets?.length}
        updateBucketSetting={(settings: Partial<BucketCustomization>) =>
          updateBucketCustomizationSetting(settings, index)
        }
      />
    </li>
  ));

  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="flex flex-col gap-8" data-test="buckets-section">
      <div className="flex w-full gap-8">
        <div className="flex w-full gap-8">
          <SettingLabel text="Buckets" className="w-fit" />
          {isLoadingBuckets && (
            <InstrumentedWaveLoader name="buckets-loading" size="small" />
          )}
        </div>
        <SemanticLegendsPaletteWithCategoricalConfig />
        <SemanticLegendBucketsActionsWithCategoricalConfig
          setIsOpen={setIsOpen}
          isOpen={isOpen}
        />
      </div>
      <ul className="flex flex-col gap-8">{bucketCustomizationList}</ul>
    </div>
  );
};
